import {relativeDate} from "../../../util/timeConverter";
import React, {useEffect, useState, useCallback, useMemo} from "react";
import {Ban} from "../interfaces";
import {BodyCell, BodyRow, BodyRowMapper, HeaderCell, HeaderRow, Table} from "../../../components/Table";
import {useOverlay} from "../overlay/OverlayContext";
import {useRPC, useSocket} from "../SocketContext";
import {rpc_getBanList, rpc_syncBanList} from "../rpc";
import Toggle from "../../../components/Toggle";
import {useLocalStorage} from "../../../hooks/useLocalStorage";
import Button from "../../../components/Button";
import {LucidePlus, LucideRefreshCcw} from "lucide-react";
import {useServerId} from "../useServerId";
import {doFetch} from "../../../util/fetch";

export default function Bans(){

    const socket = useSocket()
    const overlay = useOverlay()
    const serverId = useServerId()

    const [bans, setBans] = useState<Ban[]>([])
    const [pageInfo, setPageInfo] = useState<{ page: number, hasNextPage: boolean, banCount: number }>({ page: 0, hasNextPage: false, banCount: -1 })
    const [isLoading, setIsLoading] = useState(false)

    // Search state
    const [searchQuery, setSearchQuery] = useState<string>('')
    const [searchField, setSearchField] = useState<string>('displayName')
    const [isSearching, setIsSearching] = useState(false)
    const [searchResults, setSearchResults] = useState<Ban[]>([])
    const [showSearchResults, setShowSearchResults] = useState(false)

    const [displayMode, setDisplayMode] = useLocalStorage("displayMode")

    const fetchPage = useCallback(async (page: number) => {
        if (!socket.isConnected) return
        
        setIsLoading(true)
        const banList = await rpc_getBanList(socket, page)
        setIsLoading(false)

        if (banList) {
            //console.log("Got ban list", banList)
            setBans(prev => page === 0 ? banList.bans : [...prev, ...banList.bans])
            setPageInfo({ page, hasNextPage: banList.hasNextPage, banCount: banList.banCount })
        }
    }, [socket])

    useEffect(() => {
        if (socket.isConnected && bans.length === 0){
            fetchPage(0)
        }
    }, [socket, bans, fetchPage])

    useRPC(socket, "OnPlayerAction", (action: string, steamId: string, text?: string) => {
        //console.log("OnPlayerAction (Ban list)", action, steamId, text)
        if (action === "ban" || action === "unban"){
            setBans([]) // This triggers a reload of the ban list
        }
    })

    const loadNextPage = useCallback(() => {
        if (showSearchResults) return

        if (pageInfo.hasNextPage && !isLoading) {
            fetchPage(pageInfo.page + 1)
        }
    }, [pageInfo, isLoading, fetchPage, showSearchResults])

    // Handle search
    const handleSearch = useCallback(async (query: string) => {
        if (!query.trim()) {
            setShowSearchResults(false)
            return
        }

        setIsSearching(true)
        setShowSearchResults(true)

        try {
            const response = await doFetch<{ bans: Ban[] }>(
                `/api/client/bans/search?serverId=${serverId}&q=${encodeURIComponent(query)}&field=${searchField}&limit=50`
            )

            if (response.data) {
                setSearchResults(response.data.bans)
            }
        } catch (error) {
            console.error('Search error:', error)
        } finally {
            setIsSearching(false)
        }
    }, [serverId, searchField])

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchQuery) {
                handleSearch(searchQuery)
            } else {
                setShowSearchResults(false)
            }
        }, 300)

        return () => clearTimeout(timer)
    }, [searchQuery, handleSearch])

    const displayedBans = useMemo(() => {
        return showSearchResults ? searchResults : bans;
    }, [showSearchResults, searchResults, bans]);

    const [syncing, setSyncing] = useState(false)

    const doSync = async () => {
        setSyncing(true)

        const result = await rpc_syncBanList(socket)
        if (result?.success){
            await fetchPage(0)
        }

        setSyncing(false)
    }

    return (
        <div className="size-full flex flex-col p-4 relative">

            <div className="flex justify-between items-center mb-4">
                <div className="self-start">
                    <h1 className="text-2xl font-bold text-white">Ban List</h1>
                    {showSearchResults ? (
                        <span><b>{searchResults.length}</b> search results for "{searchQuery}" in {searchField}</span>
                    ) : (
                        <span><b>{pageInfo.banCount === -1 ? bans.length : pageInfo.banCount}</b> Banned Players ({bans.length} loaded)</span>
                    )}

                    <div className="mt-2">
                        <Toggle label="Compact Mode" checked={displayMode === "compact"}
                                onChange={e => setDisplayMode(e.currentTarget.checked ? "compact" : "normal")}/>
                    </div>
                </div>
                <div className="mt-2 self-end flex flex-col items-end gap-4">
                    <div className="flex flex-row gap-2 items-center">
                        <Button text="" className="bg-green-600" onClick={() => overlay.openBanOverlay({ steamId: "", displayName: "" })} >
                            <LucidePlus size={18} className="me-2" /> Ban Player
                        </Button>
                        <Button text="" loading={syncing} className="bg-primary" onClick={doSync} >
                            <LucideRefreshCcw size={18} className="me-2" /> Force Sync
                        </Button>
                    </div>
                        <div className="flex border border-neutral-500 bg-neutral-800 rounded focus-within:outline-none focus-within:ring-2 focus-within:ring-primary focus-within:border-primary">
                            <select
                                value={searchField}
                                onChange={(e) => setSearchField(e.target.value)}
                                className="relative border-0 bg-neutral-800 text-white rounded p-2 placeholder:text-neutral-500 focus:outline-none">
                                <option value="displayName">Display Name</option>
                                <option value="steamId">Steam ID</option>
                                <option value="reason">Reason</option>
                            </select>
                            <div className="relative min-w-64">
                                <input
                                    type="text"
                                    placeholder="Search Bans..."
                                    className="w-full ps-3 border-l rounded-l-none border-0 border-neutral-500 bg-neutral-800 text-white rounded p-2 placeholder:text-neutral-500 focus:outline-none"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && searchQuery.trim()) {
                                            handleSearch(searchQuery)
                                        }
                                    }}
                                />
                                {searchQuery && (
                                    <button
                                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                                        onClick={() => {
                                            setSearchQuery('')
                                            setShowSearchResults(false)
                                        }}
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                        </div>
                </div>
            </div>

            <Table onScrollBottom={loadNextPage}>
                <HeaderRow>
                    <HeaderCell sortKey="steamId">Steam ID</HeaderCell>
                    <HeaderCell>Display Name</HeaderCell>
                    <HeaderCell>Reason</HeaderCell>
                    <HeaderCell sortKey="expiry">Expiry</HeaderCell>
                    <HeaderCell sortKey="author">Banned by</HeaderCell>
                    <HeaderCell sortKey="timestamp">Timestamp</HeaderCell>
                </HeaderRow>
                <BodyRowMapper hideEmpty={isLoading || isSearching || syncing} placeholder="No bans on record" data={displayedBans} map={ban => (
                    <BodyRow key={ban.steamId} onClick={() => overlay.openPlayerOverlay(ban.steamId)}>
                        <BodyCell>{ban.steamId}</BodyCell>
                        <BodyCell>{ban.displayName}</BodyCell>
                        <BodyCell>{ban.reason}</BodyCell>
                        <BodyCell>{ban.expiry > 0 ? relativeDate(ban.expiry) : "never"}</BodyCell>
                        <BodyCell>{ban.author ?? <i className="text-gray-400">Console</i>}</BodyCell>
                        <BodyCell>{ban.timestamp > 0 ? relativeDate(ban.timestamp) : "-"}</BodyCell>
                    </BodyRow>
                )} />
                {(isLoading || isSearching || syncing) && (
                    <BodyRow>
                        <td colSpan={6}>
                            <div className="flex justify-center items-center py-4">
                                <span className="text-gray-400">
                                    {isSearching ? 'Searching bans...' : 'Loading bans...'}
                                </span>
                            </div>
                        </td>
                    </BodyRow>
                )}
            </Table>

        </div>
    )
}